// markup/MarkTypePicker.jsx — choose or change the catalogue type of one mark,
// or of several selected marks at once.
// ---------------------------------------------------------------------------
// 2026-09-15 — Brett: "Can you allow me to double click an area and assign it to
// one of the shortcut measures. and change it from one to another".
// 2026-10-21 — Brett: "we should be able to select multiple areas and change its
// type at once." The heading says how many marks a pick changes.
//
// A small menu at the pointer listing the tenant's types that fit the mark, the
// current one ticked (when every mark shares it), plus "No type" when any has
// one. Rendered on document.body:
// the markup layer sits inside the plan's scaled transform, and a menu inside it
// would scale with the zoom. Styled as the markup toolbar (chrome tokens); the
// swatches are the types' own colours, which are data.
// ---------------------------------------------------------------------------

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';

const GAP = 6;  // menu offset from the pointer, px
const EDGE = 8; // keep this far inside the window, px

export default function MarkTypePicker({
  x, y, types, currentKey = null, title = 'Mark type', canClear = !!currentKey, anchor = null, onPick, onClose,
}) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x + GAP, top: y + GAP });
  // Callbacks read through a ref, so the listeners below bind once and the
  // focus is set once — not again on every render of the layer. `anchor` is the
  // button that opened the menu, if any: a press on it is its own toggle, not a
  // press outside.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const anchorRef = useRef(anchor);
  anchorRef.current = anchor;

  // Keep the whole menu on screen when the double-click is near an edge.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.max(EDGE, Math.min(x + GAP, window.innerWidth - r.width - EDGE)),
      top: Math.max(EDGE, Math.min(y + GAP, window.innerHeight - r.height - EDGE)),
    });
  }, [x, y, types.length]);

  useEffect(() => {
    const onDown = (e) => {
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      if (ref.current && !ref.current.contains(e.target)) closeRef.current();
    };
    // Capture, and stop it: Escape here closes the menu, it does not also drop
    // the selection and the tool (markup's own Escape handler).
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); closeRef.current(); } };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    const el = ref.current;
    const first = el && (el.querySelector('[aria-checked="true"]') || el.querySelector('button'));
    if (first) first.focus();
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, []);

  const item = (key, label, color, on, pick) => (
    <button key={key} type="button" role="menuitemradio" aria-checked={on} onClick={pick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 32, padding: '0 10px',
        border: 'none', borderRadius: 6, background: on ? 'rgba(255,255,255,0.10)' : 'transparent',
        color: 'var(--chrome-text)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
        cursor: 'pointer', textAlign: 'left',
      }}>
      <span style={{
        width: 12, height: 12, borderRadius: 3, flex: '0 0 auto',
        background: color || 'transparent', border: color ? 'none' : '1px dashed rgba(255,255,255,0.45)',
      }} />
      <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{label}</span>
      <Check size={14} strokeWidth={2.4} style={{ visibility: on ? 'visible' : 'hidden', flex: '0 0 auto' }} />
    </button>
  );

  return createPortal(
    <div ref={ref} role="menu" aria-label="Mark type"
      // The menu is in the plan's React tree: a press here must not reach the
      // plan (a pan, a pin) or the markup surface.
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', left: pos.left, top: pos.top, zIndex: 1500, minWidth: 180,
        background: 'var(--chrome-800)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10,
        padding: 6, boxShadow: 'var(--shadow-3)', fontFamily: 'var(--font-ui)',
      }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--chrome-muted)', padding: '2px 10px 6px', letterSpacing: 0.3 }}>
        {title}
      </div>
      {types.map((t) => item(t.key, t.label, t.color, t.key === currentKey, () => onPick(t)))}
      {canClear ? item('__none', 'No type', null, false, () => onPick(null)) : null}
    </div>,
    document.body,
  );
}
